/**
 * Tells whoever looks after the tills when one of them is in trouble: sales
 * the server refused are sitting on it, or sales have been waiting to upload
 * longer than the shop's warning time. Every till reports this about itself
 * (see sync.tsx); this reads what they said, on every screen, for the people
 * who can do something about it.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../state/auth.js';
import { useT } from '../i18n/index.js';

interface TillHealth {
  id: string;
  name: string;
  pending: number;
  stuck: number;
  oldestPendingAt: string | null;
  needsAttention: boolean;
}

export function TillAlert() {
  const t = useT();
  const { can, user } = useAuth();
  const [trouble, setTrouble] = useState<TillHealth[]>([]);
  const allowed = !!user && can('devices.view');

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    const load = () =>
      api<TillHealth[]>('/admin/devices/health')
        .then((rows) => alive && setTrouble(rows.filter((r) => r.needsAttention)))
        .catch(() => undefined);
    void load();
    const timer = window.setInterval(load, 5 * 60_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [allowed]);

  if (!allowed || trouble.length === 0) return null;
  const hours = (iso: string | null) => (iso ? Math.max(1, Math.round((Date.now() - Date.parse(iso)) / 3_600_000)) : 0);
  return (
    <div className="note-warn till-alert" role="alert">
      <span>
        {trouble
          .map((d) =>
            d.stuck > 0
              ? t('tills.alertStuck', { till: d.name, n: d.stuck })
              : t('tills.alertWaiting', { till: d.name, n: d.pending, h: hours(d.oldestPendingAt) }),
          )
          .join(' · ')}
      </span>
      <Link to="/devices">{t('tills.see')}</Link>
    </div>
  );
}
